import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  Box,
  Flex,
  Heading,
  Text,
  Button,
  Checkbox,
  Input,
  Progress,
  Badge,
  Spinner,
  useToast,
  useTheme,
} from "@chakra-ui/react";
import { useSupplierRetailer } from "../../../wrappers/SupplierRetailerContext";

const RETAILER_LABELS = {
  PNP: "Pick n Pay",
  CHK: "Checkers",
  DIS: "Dis-Chem",
  MAK: "Makro",
  CLI: "Clicks",
  MED: "MediRite",
  PET: "Petshop",
};

const ALL_RETAILERS = ["PNP", "CHK", "DIS", "MAK", "CLI", "MED", "PET"];

const BatchReports = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [selectedSuppliers, setSelectedSuppliers] = useState([]);
  const [selectedRetailers, setSelectedRetailers] = useState([]);
  const [email, setEmail] = useState("helenamarais@outlook.com");
  const [isLoading, setIsLoading] = useState(true);
  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState(null);
  const esRef = useRef(null);
  const toast = useToast();
  const theme = useTheme();
  const { isMaster, selectedOrg } = useSupplierRetailer();

  // Scope the list to the master's organisation toggle — 3M must not show
  // under Spear Management. Suppliers without an org stay visible everywhere.
  // Membership, not just the home org (2026-09-17): a supplier made visible in a
  // second organisation shows under that toggle too (`org_codes`, home first).
  const visibleSuppliers =
    isMaster && selectedOrg
      ? suppliers.filter((s) => {
          const orgs = Array.isArray(s.org_codes) && s.org_codes.length ? s.org_codes : s.org_code ? [s.org_code] : [];
          return orgs.length === 0 || orgs.includes(selectedOrg);
        })
      : suppliers;

  useEffect(() => {
    fetchSuppliers();
    return () => {
      if (esRef.current) {
        esRef.current.close();
      }
    };
  }, []);

  // Toggling the org must drop selections that are no longer in view.
  useEffect(() => {
    setSelectedSuppliers((prev) => prev.filter((code) => visibleSuppliers.some((s) => s.code === code)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrg, suppliers]);

  const fetchSuppliers = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/batch-reports/suppliers-and-access`
      );
      setSuppliers(response.data.data || []);
      setIsLoading(false);
    } catch (error) {
      toast({
        title: "Failed to load suppliers",
        description: error?.response?.data?.message || "An error occurred.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      setIsLoading(false);
    }
  };

  const toggleSupplier = (code) => {
    setSelectedSuppliers((prev) =>
      prev.includes(code) ? prev.filter((s) => s !== code) : [...prev, code]
    );
  };

  const toggleRetailer = (code) => {
    setSelectedRetailers((prev) =>
      prev.includes(code) ? prev.filter((r) => r !== code) : [...prev, code]
    );
  };

  const selectAllSuppliers = () => {
    if (selectedSuppliers.length === visibleSuppliers.length) {
      setSelectedSuppliers([]);
    } else {
      setSelectedSuppliers(visibleSuppliers.map((s) => s.code));
    }
  };

  const selectAllRetailers = () => {
    if (selectedRetailers.length === ALL_RETAILERS.length) {
      setSelectedRetailers([]);
    } else {
      setSelectedRetailers([...ALL_RETAILERS]);
    }
  };

  const getValidComboCount = () => {
    let count = 0;
    for (const code of selectedSuppliers) {
      const supplier = suppliers.find((s) => s.code === code);
      if (!supplier) continue;
      for (const retailer of selectedRetailers) {
        if (supplier.retailers[retailer]) count++;
      }
    }
    return count;
  };

  const handleSubmit = async () => {
    if (selectedSuppliers.length === 0 || selectedRetailers.length === 0) {
      toast({
        title: "Selection required",
        description: "Please select at least one supplier and one retailer.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!email || !email.includes("@")) {
      toast({
        title: "Valid email required",
        description: "Please enter a valid email address.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsRunning(true);
    setProgress({ total: 0, completed: 0, failed: 0 });

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/batch-reports/start`,
        {
          suppliers: selectedSuppliers,
          retailers: selectedRetailers,
          email,
          userid: localStorage.getItem("userid"),
        }
      );

      const { jobId } = response.data;

      // EventSource cannot send an Authorization header — pass the token as a
      // query param, which requireAuth accepts as a fallback for SSE.
      const _t = localStorage.getItem("accessToken");
      const es = new EventSource(
        `${process.env.REACT_APP_API_URL}/batch-reports/progress/${jobId}${_t ? `?access_token=${encodeURIComponent(_t)}` : ""}`
      );
      esRef.current = es;

      es.addEventListener("init", (e) => {
        const data = JSON.parse(e.data);
        setProgress((prev) => ({ ...prev, total: data.total }));
        toast({
          title: `Batch started`,
          description: `Generating ${data.total} reports...`,
          status: "info",
          duration: 4000,
          isClosable: true,
        });
      });

      es.addEventListener("progress", (e) => {
        const data = JSON.parse(e.data);
        setProgress((prev) => ({
          ...prev,
          completed: data.completed,
          failed: data.failed,
          total: data.total,
        }));
        toast({
          title:
            data.status === "done"
              ? `${data.suppliercode} / ${data.retailer}`
              : `Failed: ${data.suppliercode} / ${data.retailer}`,
          status: data.status === "done" ? "success" : "error",
          duration: 3000,
          isClosable: true,
        });
      });

      es.addEventListener("zipping", () => {
        toast({
          title: "Creating zip archive...",
          status: "info",
          duration: 2000,
          isClosable: true,
        });
      });

      es.addEventListener("emailing", () => {
        toast({
          title: "Sending email...",
          status: "info",
          duration: 2000,
          isClosable: true,
        });
      });

      es.addEventListener("complete", (e) => {
        const data = JSON.parse(e.data);
        setProgress((prev) => ({
          ...prev,
          completed: data.completed,
          failed: data.failed,
          total: data.total,
        }));
        toast({
          title: "Batch complete!",
          description: `${data.completed}/${data.total} reports emailed to ${data.email}`,
          status: "success",
          duration: 10000,
          isClosable: true,
        });
        setIsRunning(false);
        es.close();
        esRef.current = null;
      });

      es.addEventListener("error", (e) => {
        try {
          const data = JSON.parse(e.data);
          toast({
            title: "Batch error",
            description: data.message,
            status: "error",
            duration: 8000,
            isClosable: true,
          });
        } catch (_) {}
        setIsRunning(false);
        es.close();
        esRef.current = null;
      });

      es.onerror = () => {
        if (es.readyState === EventSource.CLOSED) {
          setIsRunning(false);
          esRef.current = null;
        }
      };
    } catch (error) {
      toast({
        title: "Failed to start batch",
        description: error?.response?.data?.message || "An error occurred.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      setIsRunning(false);
    }
  };

  const validComboCount = getValidComboCount();
  const progressPercent =
    progress && progress.total > 0
      ? Math.round(((progress.completed + progress.failed) / progress.total) * 100)
      : 0;

  if (isLoading) {
    return (
      <Flex justify="center" align="center" mt={32}>
        <Spinner size="lg" />
      </Flex>
    );
  }

  return (
    <Box mt={16} mx="auto" maxW="1200px" px={6}>
      <Heading size="lg" mb={6}>
        Batch Sales Tracker Reports
      </Heading>

      <Flex direction={{ base: "column", md: "row" }} gap={6}>
        {/* Suppliers */}
        <Box flex="1" border="1px" borderColor="gray.200" borderRadius="md" p={5}>
          <Flex justify="space-between" align="center" mb={4}>
            <Text fontWeight="bold" fontSize="md">
              Suppliers ({selectedSuppliers.length}/{visibleSuppliers.length})
            </Text>
            <Button size="xs" variant="outline" onClick={selectAllSuppliers}>
              {selectedSuppliers.length === visibleSuppliers.length ? "Deselect All" : "Select All"}
            </Button>
          </Flex>
          <Box maxH="calc(100vh - 340px)" minH="300px" overflowY="auto" pr={2}>
            {visibleSuppliers.map((s) => (
              <Flex
                key={s.code}
                align="center"
                py={2}
                px={3}
                borderRadius="md"
                _hover={{ bg: "gray.50" }}
                cursor="pointer"
                onClick={() => toggleSupplier(s.code)}
              >
                <Checkbox
                  isChecked={selectedSuppliers.includes(s.code)}
                  onChange={() => toggleSupplier(s.code)}
                  mr={3}
                  pointerEvents="none"
                />
                <Text fontSize="sm">{s.name || s.code}</Text>
              </Flex>
            ))}
          </Box>
        </Box>

        {/* Retailers */}
        <Box flex="1" border="1px" borderColor="gray.200" borderRadius="md" p={5}>
          <Flex justify="space-between" align="center" mb={4}>
            <Text fontWeight="bold" fontSize="md">
              Retailers ({selectedRetailers.length}/{ALL_RETAILERS.length})
            </Text>
            <Button size="xs" variant="outline" onClick={selectAllRetailers}>
              {selectedRetailers.length === ALL_RETAILERS.length ? "Deselect All" : "Select All"}
            </Button>
          </Flex>
          <Box>
            {ALL_RETAILERS.map((code) => (
              <Flex
                key={code}
                align="center"
                py={2}
                px={3}
                borderRadius="md"
                _hover={{ bg: "gray.50" }}
                cursor="pointer"
                onClick={() => toggleRetailer(code)}
              >
                <Checkbox
                  isChecked={selectedRetailers.includes(code)}
                  onChange={() => toggleRetailer(code)}
                  mr={3}
                  pointerEvents="none"
                />
                <Text fontSize="sm" fontWeight="medium">{code}</Text>
                <Text fontSize="sm" color="gray.500" ml={2}>
                  {RETAILER_LABELS[code]}
                </Text>
              </Flex>
            ))}
          </Box>
        </Box>

        {/* Actions */}
        <Box flex="1" border="1px" borderColor="gray.200" borderRadius="md" p={5}>
          <Text fontWeight="bold" fontSize="md" mb={4}>
            Send Reports
          </Text>

          <Text fontSize="sm" fontWeight="medium" mb={1}>
            Email to
          </Text>
          <Input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="recipient@email.com"
            size="sm"
            mb={5}
          />

          {selectedSuppliers.length > 0 && selectedRetailers.length > 0 && (
            <Box mb={5}>
              <Text fontSize="sm" color="gray.600">
                {validComboCount} report(s) will be generated
              </Text>
              {validComboCount < selectedSuppliers.length * selectedRetailers.length && (
                <Text fontSize="xs" color="orange.500" mt={1}>
                  Some suppliers don't have access to all selected retailers
                </Text>
              )}
            </Box>
          )}

          <Button
            colorScheme="teal"
            width="100%"
            onClick={handleSubmit}
            isDisabled={isRunning || selectedSuppliers.length === 0 || selectedRetailers.length === 0}
            isLoading={isRunning}
            loadingText="Generating..."
            mb={5}
          >
            Generate & Email Reports
          </Button>

          {/* Progress */}
          {progress && progress.total > 0 && (
            <Box>
              <Flex justify="space-between" mb={2}>
                <Text fontWeight="bold" fontSize="sm">
                  Progress
                </Text>
                <Flex gap={2}>
                  <Badge colorScheme="green">{progress.completed} done</Badge>
                  {progress.failed > 0 && (
                    <Badge colorScheme="red">{progress.failed} failed</Badge>
                  )}
                  <Badge colorScheme="gray">{progress.total} total</Badge>
                </Flex>
              </Flex>
              <Progress
                value={progressPercent}
                colorScheme="teal"
                size="sm"
                borderRadius="md"
                hasStripe={isRunning}
                isAnimated={isRunning}
              />
              <Text fontSize="xs" color="gray.500" mt={1} textAlign="right">
                {progressPercent}%
              </Text>
            </Box>
          )}
        </Box>
      </Flex>
    </Box>
  );
};

export default BatchReports;
